// Detailseite eines Serienmail-Laufs: Fortschritt aus baueBilanz plus Empfaengertabelle.
// Solange der Lauf 'laeuft', wird alle 5 Sekunden nachgeladen — kein Realtime-Kanal,
// das waere fuer einen Fortschrittsbalken zu viel Apparat.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowCounterClockwise, CaretLeft, CircleNotch, PlayCircle, Prohibit, WarningCircle } from '@phosphor-icons/react';
import { BestaetigenDialog, Chip, ImmoButton, LeerZustand } from '../../../components/immobilien/ui';
import { EMPFAENGER_STATUS, LAUF_STATUS, zielgruppenText, type LaufZielgruppe, type LaufStatus } from '../../../components/immobilien/email/serienmailAnzeige';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { Fortschrittsbalken } from './Fortschrittsbalken';
import { SERIENMAIL_PFAD } from './SerienmailListe';
import { supabase } from '../../../lib/supabase/client';
import { baueBilanz, type EmpfaengerStatus } from '../../../lib/serienmail/fortschritt';
import { setzeFehlerZurueck, starteVersand } from '../../../lib/serienmail/api';

interface Lauf {
  id: string;
  betreff: string;
  status: LaufStatus;
  zielgruppe: LaufZielgruppe;
  zielgruppen?: string[] | null;
  created_at: string;
  gestartet_am: string | null;
  beendet_am: string | null;
  // Fix-Runde 5 N3: wird gebraucht, um zu erkennen, ob die Selbstaufruf-Kette noch lebt —
  // der Worker schreibt updated_at bei jedem Paket mit.
  updated_at: string | null;
}

interface EmpfaengerZeile {
  id: string;
  name: string;
  email: string;
  status: EmpfaengerStatus;
  fehler_text: string | null;
}

/** laufId: im E-Mail-Bereich (?ordner=serienmail&lauf=<id>), sonst aus der Route /admin/serienmail/:id. */
const SerienmailDetail: React.FC<{ laufId?: string }> = ({ laufId }) => {
  const { id: routenId } = useParams<{ id: string }>();
  const id = laufId ?? routenId;
  const navigate = useNavigate();
  const location = useLocation();
  const [lauf, setLauf] = useState<Lauf | null>(null);
  const [zeilen, setZeilen] = useState<EmpfaengerZeile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Kommt vom Assistenten, wenn legeSerienmailAn zwar geklappt hat, aber der
  // anschliessende starteVersand-Aufruf scheiterte (SchrittPruefen.tsx) — der Lauf
  // existiert bereits, ohne diesen Hinweis wuesste hier niemand, dass er noch nicht
  // gestartet ist.
  const [startFehler, setStartFehler] = useState<string | null>(() => (location.state as { startFehler?: string } | null)?.startFehler ?? null);
  const [wirdGestartet, setWirdGestartet] = useState(false);
  // Dezenter Hinweis fuer einen gescheiterten Hintergrund-Nachladeversuch: der
  // bisherige Stand bleibt sichtbar, es soll nur auffallen, dass er gerade nicht
  // aktualisiert werden konnte — kein Banner, das den Inhalt verdraengt.
  const [nachladenKlemmt, setNachladenKlemmt] = useState(false);
  const [zurueckSetzen, setZurueckSetzen] = useState(false);
  const [wirdAbgebrochen, setWirdAbgebrochen] = useState(false);
  // Rückfrage vor dem Abbruch als Dialog statt window.confirm (Entscheidung 8, gleicher Text).
  const [abbruchFrage, setAbbruchFrage] = useState(false);
  const { mobil } = useMobilModus();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Letzter bekannter Laufstatus, unabhaengig davon, ob der letzte Ladeversuch
  // geklappt hat — die Timer-Planung haengt daran, nicht an einem frischen
  // setLauf-Aufruf (siehe Kommentar unten).
  const statusRef = useRef<LaufStatus | null>(null);
  // Haelt fest, ob die Komponente noch eingehaengt ist. Noetig, weil die
  // Aufraeumfunktion des Effekts nur den bereits GEPLANTEN Timer loeschen kann —
  // nicht aber eine Abfrage, die beim Aushaengen noch unterwegs ist. Ohne diese
  // Pruefung wuerde eine solche Abfrage trotzdem aufloesen, im finally-Block
  // unbedingt einen neuen Timer planen (den keine Aufraeumfunktion mehr erfasst),
  // der wieder laden() aufruft, der wieder einen plant — das Polling liefe dann
  // nach dem Verlassen der Seite unbegrenzt im Hintergrund weiter.
  const eingehaengtRef = useRef(true);

  const laden = useCallback(async (mitLadeAnzeige: boolean) => {
    if (!id) return;
    if (mitLadeAnzeige) {
      setLoading(true);
      setError(null);
    }
    try {
      const [{ data: laufDaten, error: laufFehler }, { data: zeilenDaten, error: zeilenFehler }] = await Promise.all([
        supabase.from('serienmails').select('id, betreff, status, zielgruppe, zielgruppen, created_at, gestartet_am, beendet_am, updated_at').eq('id', id).single(),
        supabase.from('serienmail_empfaenger').select('id, name, email, status, fehler_text').eq('serienmail_id', id).order('name'),
      ]);
      if (laufFehler) throw laufFehler;
      if (zeilenFehler) throw zeilenFehler;
      if (!eingehaengtRef.current) return;
      const geladenerLauf = laufDaten as Lauf;
      setLauf(geladenerLauf);
      setZeilen((zeilenDaten || []) as EmpfaengerZeile[]);
      statusRef.current = geladenerLauf.status;
      setNachladenKlemmt(false);
    } catch (err: any) {
      console.error('Fehler beim Laden des Serienmail-Laufs:', err);
      if (!eingehaengtRef.current) return;
      if (mitLadeAnzeige) {
        // Erst-/manueller Ladevorgang: es gibt noch keinen Stand zum Weiterzeigen,
        // also der volle Fehlerbanner.
        setError(err.message);
      } else {
        // Stiller Nachladeversuch waehrend des Pollings: den bisherigen Stand NICHT
        // verwerfen (statusRef bleibt unveraendert), nur dezent markieren.
        setNachladenKlemmt(true);
      }
    } finally {
      if (mitLadeAnzeige && eingehaengtRef.current) setLoading(false);
      // Den naechsten Versuch IMMER planen, egal ob dieser hier geklappt hat oder
      // nicht — sonst friert ein einzelner Netz-Hänger oder DB-Timeout das Polling
      // fuer immer ein: der vorige Fix haengte den Timer an die Objektreferenz von
      // `lauf`, die bei einem gescheiterten Nachladen (kein setLauf-Aufruf) unveraendert
      // blieb, wodurch der Effekt nie erneut lief und nie ein neuer Timer entstand.
      // Ein 20-Minuten-Lauf wirkte dann haengengeblieben, obwohl er im Hintergrund
      // normal weiterlief. Deshalb haengt die Planung hier an statusRef (letzter
      // bekannter Stand) statt am Erfolg dieses einzelnen Aufrufs.
      //
      // Aber NUR planen, solange die Komponente noch eingehaengt ist — sonst
      // ueberlebt das Polling das Verlassen der Seite (siehe Kommentar bei
      // eingehaengtRef oben).
      if (timerRef.current) clearTimeout(timerRef.current);
      if (eingehaengtRef.current && statusRef.current === 'laeuft') {
        timerRef.current = setTimeout(() => laden(false), 5000);
      }
    }
  }, [id]);

  useEffect(() => {
    eingehaengtRef.current = true;
    laden(true);
    return () => {
      eingehaengtRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [laden]);

  const bilanz = baueBilanz(zeilen);

  // Fix-Runde 5 N3: Der Status 'laeuft' allein taugt nicht als Sperre fuer die Aktionsknoepfe.
  // Stirbt die Selbstaufruf-Kette (Invocation weg), bleibt der Lauf auf 'laeuft' stehen und
  // war mit KEINEM Bedienpfad mehr zu bewegen: "Jetzt starten" verlangt offene Zeilen,
  // "Fehlgeschlagene erneut senden" war durch die 'laeuft'-Sperre blockiert, und auch das
  // Rettungsnetz greift nur bei offenen oder haengenden Zeilen. Deshalb dieselbe Schwelle wie
  // die Bremse im Worker (KETTE_LEBT_MS in supabase/functions/serienmail-versand/index.ts,
  // 3 Minuten): Der Worker schreibt updated_at bei jedem Paket, also im Minutentakt, mit — ist
  // der Zeitstempel aelter, gilt die Kette als tot und die Knoepfe erscheinen wieder. Beide
  // Schwellen gehoeren zusammen und muessen gemeinsam geaendert werden.
  const KETTE_LEBT_MS = 3 * 60_000;
  const ketteLebt = lauf?.status === 'laeuft'
    && !!lauf.updated_at
    && Date.now() - new Date(lauf.updated_at).getTime() < KETTE_LEBT_MS;

  const handleErneutSenden = async () => {
    if (!id) return;
    setZurueckSetzen(true);
    try {
      // setzeFehlerZurueck setzt nur die Zeilen zurueck; steht der Lauf schon auf
      // 'fertig', ruehrt ihn danach kein Cron mehr an — also den Versand explizit
      // wieder anstossen, sonst bliebe der Knopf wirkungslos.
      await setzeFehlerZurueck(id);
      await starteVersand(id);
      await laden(false);
    } catch (err: any) {
      console.error('Fehler beim erneuten Senden:', err);
      setError(err.message);
    } finally {
      setZurueckSetzen(false);
    }
  };

  // "Jetzt starten" ist der Weg, einen Lauf anzustossen, der noch unerledigte
  // Empfaenger hat: Status 'entwurf' (erster Startversuch aus dem Assistenten
  // scheiterte), 'geplant' (soll nun doch sofort raus) — oder, siehe Bedingung
  // beim Knopf unten, ein Lauf, der bereits auf 'fertig' steht, dessen Zeilen aber
  // wieder offen sind.
  const handleJetztStarten = async () => {
    if (!id) return;
    setWirdGestartet(true);
    setStartFehler(null);
    try {
      await starteVersand(id);
      await laden(false);
    } catch (err: any) {
      setStartFehler(err.message || 'Versand konnte nicht gestartet werden.');
    } finally {
      setWirdGestartet(false);
    }
  };

  // Abschluss-Review I4: Not-Aus. Der Zustand 'abgebrochen' war ueberall vorgesehen
  // (Migration, Label, Farbe, Ausstieg im Worker), aber KEIN Codepfad setzte ihn je — ein
  // Rundschreiben an 400 Menschen laeuft rund 20 Minuten, und wenn in Minute 3 der falsche
  // Verteiler oder Betreff auffaellt, gab es keine Moeglichkeit zu stoppen. Der Worker
  // steigt bei 'abgebrochen' beim naechsten Paket ohne einen weiteren Versand aus.
  const handleAbbrechen = async () => {
    if (!id || !lauf) return;
    // Fix-Runde 5 N4(b): der Text verspricht nicht mehr, als der Abbruch halten kann. Der
    // Worker prueft den Abbruch zwar vor jeder einzelnen Zeile, eine Mail, die in genau
    // diesem Moment schon bei Graph liegt, geht aber trotzdem noch raus.
    // (Die Rückfrage mit diesem Text steht jetzt im BestaetigenDialog unten.)
    setWirdAbgebrochen(true);
    try {
      const { error: abbruchFehler } = await supabase.from('serienmails').update({ status: 'abgebrochen' }).eq('id', id);
      if (abbruchFehler) throw abbruchFehler;
      await laden(false);
    } catch (err: any) {
      console.error('Fehler beim Abbrechen des Versands:', err);
      setError(err.message || 'Versand konnte nicht abgebrochen werden.');
    } finally {
      setWirdAbgebrochen(false);
      setAbbruchFrage(false);
    }
  };

  if (loading && !lauf) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ padding: '72px 0', gap: 12 }}>
        <CircleNotch size={28} className="animate-spin" color="var(--ink3)" />
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Lade Serienmail-Lauf...</p>
      </div>
    );
  }

  if (error && !lauf) {
    return (
      <LeerZustand icon={WarningCircle} titel="Fehler beim Abrufen der Daten" text={error}>
        <ImmoButton groesse={44} onClick={() => laden(true)} style={{ marginTop: 6 }}>Erneut versuchen</ImmoButton>
      </LeerZustand>
    );
  }

  if (!lauf) return null;

  const st = LAUF_STATUS[lauf.status];
  // Sichtbarkeit der Aktionsknöpfe – Bedingungen unverändert (Abschluss-Review I2/I3(a), Fix-Runde 5 N2/N3, siehe Handler oben).
  const zeigeStarten = bilanz.offen > 0 && !ketteLebt && lauf.status !== 'abgebrochen';
  const zeigeErneut = bilanz.fehler > 0 && !ketteLebt && lauf.status !== 'abgebrochen';
  const zeigeAbbrechen = lauf.status === 'laeuft' || lauf.status === 'geplant';
  const kh = mobil ? 44 : 40;
  const kpi = (label: string, wert: number, farbe?: string) => (
    <div className="flex flex-col" style={{ gap: 4, borderRadius: 16, padding: '12px 14px', background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
      <span className="immo-zahl" style={{ fontSize: 24, fontWeight: 800, color: farbe ?? 'var(--ink)' }}>{wert}</span>
    </div>
  );

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <div className="flex">
        <ImmoButton groesse={kh} icon={CaretLeft} onClick={() => navigate(SERIENMAIL_PFAD)}>Zurück zur Übersicht</ImmoButton>
      </div>

      <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 24, padding: mobil ? 14 : 18, gap: 14 }}>
        <div className="flex items-start min-w-0" style={{ gap: 10 }}>
          <div className="flex flex-col flex-1 min-w-0" style={{ gap: 3 }}>
            <h2 className="m-0" style={{ fontSize: mobil ? 18 : 20, fontWeight: 800, letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>{lauf.betreff || '(ohne Betreff)'}</h2>
            <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{zielgruppenText(lauf.zielgruppe, lauf.zielgruppen)} · Angelegt am {new Date(lauf.created_at).toLocaleDateString('de-DE')}</span>
            {nachladenKlemmt && (
              <span className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--warn)' }}>
                <WarningCircle size={13} weight="bold" />Aktualisierung klemmt gerade — bisheriger Stand
              </span>
            )}
          </div>
          <Chip ton={st.ton} groesse="s">{st.label}</Chip>
        </div>

        {/* Fortschritt — Zahlen ausschliesslich aus baueBilanz, keine eigene Rechnung hier */}
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))', gap: 8 }}>
          {kpi('Gesamt', bilanz.gesamt)}
          {kpi('Gesendet', bilanz.gesendet, 'var(--ok)')}
          {kpi('Fehler', bilanz.fehler, bilanz.fehler > 0 ? 'var(--bad)' : undefined)}
          {kpi('Offen', bilanz.offen, 'var(--ink2)')}
        </div>
        <Fortschrittsbalken bilanz={bilanz} zweifarbig hoehe={8} />

        {zeigeStarten || zeigeErneut || zeigeAbbrechen ? (
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            {zeigeStarten && (
              <ImmoButton art="akzent" groesse={kh} icon={wirdGestartet ? CircleNotch : PlayCircle} onClick={handleJetztStarten} disabled={wirdGestartet}>Jetzt starten</ImmoButton>
            )}
            {zeigeErneut && (
              <ImmoButton art="akzent" groesse={kh} icon={zurueckSetzen ? CircleNotch : ArrowCounterClockwise} onClick={handleErneutSenden} disabled={zurueckSetzen}>Fehlgeschlagene erneut senden</ImmoButton>
            )}
            {zeigeAbbrechen && (
              <ImmoButton art="bad" groesse={kh} icon={wirdAbgebrochen ? CircleNotch : Prohibit} onClick={() => setAbbruchFrage(true)} disabled={wirdAbgebrochen}>Versand abbrechen</ImmoButton>
            )}
          </div>
        ) : null}

        {startFehler && (
          <div role="alert" className="flex items-center" style={{ gap: 8, borderRadius: 14, padding: '10px 14px', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
            <WarningCircle size={16} weight="bold" className="shrink-0" />
            <span>Angelegt, aber nicht gestartet: {startFehler}</span>
          </div>
        )}
        {error && (
          <div role="alert" className="flex items-center" style={{ gap: 8, borderRadius: 14, padding: '10px 14px', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
            <WarningCircle size={16} weight="bold" className="shrink-0" /><span>{error}</span>
          </div>
        )}
      </section>

      {/* Empfaenger */}
      <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 24, padding: mobil ? 10 : '8px 18px', gap: 0 }}>
        {zeilen.length === 0 ? (
          <span style={{ padding: 28, textAlign: 'center', fontSize: 13, color: 'var(--ink3)' }}>Keine Empfänger in diesem Lauf</span>
        ) : mobil ? (
          <div className="flex flex-col" style={{ gap: 6 }}>
            {zeilen.map((zeile) => {
              const gesendetMitHinweis = zeile.status === 'gesendet' && !!zeile.fehler_text;
              const es = EMPFAENGER_STATUS[zeile.status];
              const hinweis = (zeile.status === 'fehler' || gesendetMitHinweis) && zeile.fehler_text ? zeile.fehler_text : '';
              return (
                <div key={zeile.id} className="flex flex-col" style={{ gap: 4, borderRadius: 16, padding: 12, background: 'var(--fill)' }}>
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <span className="flex-1 min-w-0 truncate" style={{ fontSize: 14, fontWeight: 800 }}>{zeile.name}</span>
                    <Chip ton={es.ton} groesse="xs">{gesendetMitHinweis ? 'Gesendet, mit Hinweis' : es.label}</Chip>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--ink2)', overflowWrap: 'anywhere' }}>{zeile.email}</span>
                  {hinweis && <span style={{ fontSize: 12, color: 'var(--bad)' }}>{hinweis}</span>}
                </div>
              );
            })}
          </div>
        ) : (
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)', textAlign: 'left' }}>
                <th style={{ padding: '12px 8px 10px 0' }}>Name</th>
                <th style={{ padding: '12px 8px 10px' }}>Adresse</th>
                <th style={{ padding: '12px 8px 10px' }}>Status</th>
                <th style={{ padding: '12px 0 10px 8px' }}>Hinweis</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((zeile) => {
                // Ein 'gesendet' MIT fehler_text ist ein echter Vorfall: die Mail kam
                // an, aber die Protokollierung ist gescheitert. Ohne diese Kennzeichnung
                // waere er in der Tabelle unsichtbar, weil der Status selbst gruen bleibt.
                const gesendetMitHinweis = zeile.status === 'gesendet' && !!zeile.fehler_text;
                const es = EMPFAENGER_STATUS[zeile.status];
                return (
                  <tr key={zeile.id} style={{ borderTop: '1px solid var(--line)' }}>
                    <td style={{ padding: '11px 8px 11px 0', fontSize: 13, fontWeight: 800 }}>{zeile.name}</td>
                    <td style={{ padding: '11px 8px', fontSize: 13, color: 'var(--ink2)', overflowWrap: 'anywhere' }}>{zeile.email}</td>
                    <td style={{ padding: '11px 8px' }}><Chip ton={es.ton} groesse="xs">{gesendetMitHinweis ? 'Gesendet, mit Hinweis' : es.label}</Chip></td>
                    <td style={{ padding: '11px 0 11px 8px', fontSize: 12, color: 'var(--bad)', maxWidth: 280 }}>
                      {(zeile.status === 'fehler' || gesendetMitHinweis) && zeile.fehler_text ? zeile.fehler_text : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {abbruchFrage && (
        <BestaetigenDialog
          titel="Versand wirklich abbrechen?"
          text={`Der Versand stoppt umgehend: ${bilanz.offen} von ${bilanz.gesamt} Empfängern haben die Mail noch nicht erhalten. `
            + `Bereits versendete Mails (${bilanz.gesendet}) bleiben versendet und lassen sich nicht zurückholen; einzelne gerade in Arbeit befindliche Mails können noch rausgehen.`}
          bestaetigenLabel="Versand abbrechen"
          icon={Prohibit}
          laeuft={wirdAbgebrochen}
          onAbbrechen={() => setAbbruchFrage(false)}
          onBestaetigen={() => { void handleAbbrechen(); }}
        />
      )}
    </div>
  );
};

export default SerienmailDetail;
